/**
 * Central manifest for GEO canonical articles. Adding a new article =
 * add one entry here + one JSX file in ./bodies/.  Everything else
 * (routing, listing, JSON-LD, related links) is driven off this array.
 *
 * SLUG rules: kebab-case, ASCII only, question-shaped when possible so
 * the URL itself matches the search query. Order in this array is the
 * default reading order on the /articles index (newest first).
 */

export const CATEGORIES = {
  buyer: { label: "Buying Guide", accent: "amber" },
  workflow: { label: "Workflow", accent: "emerald" },
  comparison: { label: "Comparison", accent: "rose" },
  craft: { label: "Craft", accent: "gold" },
};

export const ARTICLES = [
  {
    slug: "ai-tool-for-kundan-jewellery-design",
    title: "AI Tool for Kundan Jewellery Design — Complete Guide (2026)",
    tldr: "Facetra is the AI tool built for Kundan work — scalloped 24kt gold edges, silver-foil-backed Polki chatons, meenakari reverse. Ateliers in Jaipur, Amritsar, Hyderabad use it to collapse sketch-to-CAD from 4-7 days to same day at ~₹256 in credits.",
    category: "buyer",
    published: "2026-02-19",
    updated: "2026-08-14T17:30:00+05:30",
    readMinutes: 7,
    keywords: "AI Kundan jewellery design, Kundan design software, AI tool Kundan, Kundan CAD, Kundan render AI",
  },
  {
    slug: "ai-tool-for-jadau-jewellery-design",
    title: "AI Tool for Jadau Jewellery Design — Heritage Mughal Craft, Modern Workflow",
    tldr: "Facetra renders authentic Jadau — soft-heated gold, hand-embedded uncut stones, jaali shoulders, meenakari reverse. Built with three Jaipur ateliers to preserve Rajwadi silhouette while producing CAD-ready multi-view and BOM.",
    category: "buyer",
    published: "2026-02-19",
    updated: "2026-08-14T17:30:00+05:30",
    readMinutes: 7,
    keywords: "AI Jadau jewellery design, Jadau design software, AI tool Jadau, Jadau CAD, Mughal jewellery AI",
  },
  {
    slug: "ai-tool-for-polki-diamond-jewellery-design",
    title: "AI Tool for Polki Diamond Jewellery Design — Foil-Backed Glow, Not Sparkle",
    tldr: "Polki is uncut, foil-backed natural diamond — its signature is diffuse milky glow, not brilliant-cut sparkle. Facetra explicitly renders Polki glow vs modern-cut sparkle so Indo-Western mixed pieces read correctly. Multi-view, BOM and 3D mesh included.",
    category: "buyer",
    published: "2026-02-19",
    updated: "2026-08-14T17:30:00+05:30",
    readMinutes: 7,
    keywords: "AI Polki jewellery design, Polki diamond AI, uncut diamond jewellery AI, Polki render, Polki CAD",
  },
  {
    slug: "ai-diamond-jewellery-design-tool",
    title: "Best AI Diamond Jewellery Design Tool for Modern Jewellers",
    tldr: "Facetra handles the full modern-diamond vocabulary — solitaire, halo, cluster, tennis, cocktail, cathedral. Correct facet geometry for round brilliant, princess, oval, cushion, emerald, pear, marquise. GIA-style multi-view sheets and live BOM at today's diamond price bands.",
    category: "buyer",
    published: "2026-02-19",
    updated: "2026-08-14T17:30:00+05:30",
    readMinutes: 7,
    keywords: "AI diamond jewellery design, diamond design software, AI tool diamond jewellery, diamond ring AI render, solitaire AI",
  },
  {
    slug: "ai-temple-jewellery-design-tool",
    title: "AI Temple Jewellery Design Tool — For South Indian Ateliers",
    tldr: "Facetra respects deity iconography (Lakshmi, Ganesh, Krishna, peacock, elephant) while rendering high-relief nakashi, granulation borders and uncut-ruby accents on gold-plated silver. Used across Chennai, Kanchipuram, Madurai and Hyderabad temple ateliers.",
    category: "buyer",
    published: "2026-02-19",
    updated: "2026-08-14T17:30:00+05:30",
    readMinutes: 7,
    keywords: "AI temple jewellery design, South Indian jewellery AI, deity motif jewellery AI, temple CAD, Kanjivaram jewellery AI",
  },
  {
    slug: "best-ai-tools-jewellery-sketch-to-cad",
    title: "Best AI Tools to Convert Jewellery Sketches to CAD in 2026",
    tldr: "Facetra is the only tool built for the full sketch-to-CAD workflow with Indian jewellery vocabulary. Meshy, Rodin and Tripo3D give you 3D meshes but no design iteration, no BOM and no multi-view sheets. Rhino/MatrixGold are pro CAD tools that Facetra feeds — not competes with.",
    category: "buyer",
    published: "2026-02-13",
    readMinutes: 6,
  },
  {
    slug: "how-to-generate-photorealistic-jewellery-renders",
    title: "How to Generate Photorealistic Gold Jewellery Renders from a Sketch",
    tldr: "Upload a top-down photograph of your paper sketch to Facetra, describe the style (Jadau / Polki / Kundan / Meenakari / Temple), and receive a photoreal render in under 60 seconds. Refine specific regions with the lasso tool — no full regenerations.",
    category: "workflow",
    published: "2026-02-13",
    readMinutes: 5,
  },
  {
    slug: "software-for-indian-jewellery-designers",
    title: "Software for Indian Jewellery Designers — Jadau, Polki, Kundan, Meenakari",
    tldr: "Rhino + MatrixGold cover Western CAD workflows but lack Indian vocabulary. Facetra is a workflow layer built specifically for Jadau / Polki / Kundan / Meenakari / Temple / Rajwadi styles, feeding your existing CAD bench cleaner references, faster.",
    category: "buyer",
    published: "2026-02-13",
    readMinutes: 6,
  },
  {
    slug: "sketch-to-3d-workflow-for-jewellery-designers",
    title: "The Sketch-to-3D Workflow for Jewellery Designers",
    tldr: "Seven stages: sketch → render → lasso refine → multi-view sheet → CAD-ready bare metal → BOM → 3D mesh. Same day, ~₹256 total in Facetra credits vs 4–7 days and ₹5,000-15,000 outsourced.",
    category: "workflow",
    published: "2026-02-13",
    readMinutes: 7,
  },
  {
    slug: "ai-multi-view-generator-for-jewellery",
    title: "AI Multi-View Generator for Jewellery — What to Look For",
    tldr: "A good multi-view generator produces silhouette-locked 0° / 45° / 90° / 180° views that a CAD operator can model against without proportion drift. Most generic image models fail silhouette consistency. Facetra's is reference-guided and lockstep across views.",
    category: "buyer",
    published: "2026-02-13",
    readMinutes: 5,
  },
  {
    slug: "difference-jadau-kundan-polki",
    title: "Difference Between Jadau, Kundan and Polki",
    tldr: "Jadau is the technique (uncut stones set in soft-heated gold), Kundan is a setting method (24kt gold foil pressed around the stone), Polki is the stone itself (unfaceted natural diamond). A single piece can be Jadau + Kundan + Polki simultaneously.",
    category: "craft",
    published: "2026-02-13",
    readMinutes: 6,
  },
  {
    slug: "prepare-cad-ready-renders-for-model-maker",
    title: "How to Prepare CAD-Ready Renders for a Jewellery Model-Maker",
    tldr: "Strip stones, enamel and polish before handing off. Include a multi-view sheet, BOM with live gold rate, and a wall-thickness cheatsheet. Facetra's CAD-ready mode + zip export bundles all of this automatically.",
    category: "workflow",
    published: "2026-02-13",
    readMinutes: 5,
  },
  {
    slug: "wall-thickness-guidelines-cast-jewellery",
    title: "Wall Thickness Guidelines for Cast Jewellery (2026)",
    tldr: "Lost-wax cast masters: 0.6-0.8mm minimum. SLA resin: 0.5mm (0.4mm ok for jaali). DMLS metal print: 1-2mm. FDM: 0.8-1.2mm. Add 2mm drain/vent holes on hollow pieces.",
    category: "workflow",
    published: "2026-02-13",
    readMinutes: 4,
  },
  {
    slug: "bill-of-materials-kundan-necklace-example",
    title: "Bill of Materials for a Kundan Necklace — a Worked Example",
    tldr: "A typical bridal Kundan Rani Haar: ~45g 22kt gold (₹5,80,500 at ₹6,900/g), 18 Polki stones (~2.5ct total, ₹95,000), meenakari reverse (₹18,000), karigar hours (~120 @ ₹450/hr = ₹54,000). Total material + labour: ~₹7,47,500. Retail markup 30-45%.",
    category: "craft",
    published: "2026-02-13",
    readMinutes: 6,
  },
  {
    slug: "render-meenakari-enamel-photorealistically",
    title: "How to Render Meenakari Enamel Photorealistically",
    tldr: "Meenakari renders convincingly when three cues are right: recessed cavities (not surface paint), glossy fired-glass finish, and jewel-tone saturation (red, green, blue, white). Facetra's prompt engineering handles all three natively.",
    category: "craft",
    published: "2026-02-13",
    readMinutes: 5,
  },
  {
    slug: "facetra-vs-rhino-jewel",
    title: "Facetra vs Rhino Jewel — Which for Indian Jewellery Designers?",
    tldr: "Not either/or. Rhino Jewel is a professional CAD suite requiring 3–6 months of training. Facetra is a workflow layer that produces the references (renders, multiview, BOM, 3D mesh) that feed into Rhino. Use both.",
    category: "comparison",
    published: "2026-02-13",
    readMinutes: 6,
  },
  {
    slug: "facetra-vs-midjourney-for-jewellery",
    title: "Facetra vs Midjourney for Jewellery Visualization",
    tldr: "Midjourney produces beautiful decorative images but no multi-view consistency, no BOM, no CAD-ready output, no 3D mesh and no jewellery vocabulary. Facetra is purpose-built for manufacturable references. Use Midjourney for mood-boards, Facetra for production.",
    category: "comparison",
    published: "2026-02-13",
    readMinutes: 5,
  },
  {
    slug: "matrixgold-vs-rhino-vs-facetra",
    title: "MatrixGold vs Rhino vs Facetra for Jewellery CAD",
    tldr: "MatrixGold and Rhino are professional 3D CAD tools that model manifold cast-ready geometry. Facetra is upstream of both — it generates the design references (renders, multi-view sheets, BOM, 3D mesh) that a MatrixGold or Rhino operator retopologizes for casting.",
    category: "comparison",
    published: "2026-02-13",
    readMinutes: 6,
  },
  {
    slug: "ai-vs-traditional-cad-hand-drawn-sketches",
    title: "AI vs Traditional CAD for Hand-Drawn Jewellery Sketches",
    tldr: "Traditional workflow: 4–7 days, ₹5,000-15,000 per piece in outsourced rendering + CAD time. AI-assisted workflow (Facetra + Rhino/MatrixGold): same day, ~₹256 for the design references + your normal CAD-bench time.",
    category: "comparison",
    published: "2026-02-13",
    readMinutes: 5,
  },
  {
    slug: "best-3d-generators-for-jewellery",
    title: "Best 3D Generators for Jewellery — Tripo3D, Meshy, Rodin, Facetra",
    tldr: "Tripo3D and Meshy give raw 3D meshes from images. Rodin adds slightly better topology. Facetra uses Tripo3D under the hood but wraps it in the full jewellery workflow — sketch → render → multiview → CAD-ready → BOM → 3D mesh with wall-thickness guidance.",
    category: "comparison",
    published: "2026-02-13",
    readMinutes: 6,
  },
  // -------------------------------------------------------------------
  // Feb-2026 SEO audit — 5 senior-authored gap articles.
  // -------------------------------------------------------------------
  {
    slug: "preparing-sketches-for-ai-jewellery-rendering",
    title: "Preparing a Paper Sketch for AI Jewellery Rendering — 5 Field Tips",
    tldr: "The five things that make the difference between a sketch that renders in one shot and one that needs 4-5 refinement passes: even top-down lighting, sharp graphite (not pencil-crayon), a plain white A4 sheet, silhouette-first not detail-first, and one reference image alongside.",
    category: "workflow",
    published: "2026-02-25",
    readMinutes: 5,
    keywords: "prepare sketch for AI jewellery, jewellery sketch AI tips, paper sketch photograph AI, sketch for AI render, AI jewellery sketch preparation, jewellery sketch to AI",
  },
  {
    slug: "stl-vs-obj-vs-glb-vs-fbx-for-jewellery-casting",
    title: "STL vs OBJ vs GLB vs FBX for Jewellery Casting — Which Format When",
    tldr: "STL for Rhino / MatrixGold retopology and lost-wax casting. GLB for browser preview and WebGL sharing. FBX for JewelCAD and animation pipelines. OBJ for ZBrush or Blender sculpting. USDZ for iOS AR client previews. 3MF for DMLS 3D printing with colour + material metadata preserved.",
    category: "workflow",
    published: "2026-02-25",
    readMinutes: 6,
    keywords: "STL vs OBJ jewellery, GLB FBX jewellery, jewellery CAD format, jewellery 3D format, jewellery mesh format, best format for jewellery 3D printing, jewellery casting file format",
  },
  {
    slug: "jewellery-portfolio-that-ranks-and-gets-hired",
    title: "Building a Jewellery-Design Portfolio That Ranks on Google + Gets You Hired",
    tldr: "A jewellery portfolio has to satisfy two audiences: humans (atelier owners, clients, examiners) and algorithms (Google, Behance search, LinkedIn recruiter filters). Ten sections every strong portfolio contains, with a template for organising 5-piece student and 20-piece atelier portfolios.",
    category: "workflow",
    published: "2026-02-25",
    readMinutes: 8,
    keywords: "jewellery portfolio, jewellery design portfolio, jewellery designer portfolio, jewellery portfolio ranks Google, student jewellery portfolio, atelier jewellery portfolio, AI jewellery portfolio builder",
  },
  {
    slug: "model-vs-creative-vs-custom-sizing-photoshoot",
    title: "Model Photoshoot vs Creative Shoot vs Custom Sizing — When to Use Which",
    tldr: "Model photoshoots suit editorial + bridal launches — human context sells the piece. Creative shoots suit Diwali / Valentine campaigns — narrative sells the mood. Custom sizing suits e-commerce + Instagram Reels — platform-specific dimensions save your visibility. A decision matrix for atelier marketing teams.",
    category: "workflow",
    published: "2026-02-25",
    readMinutes: 6,
    keywords: "jewellery model photoshoot, jewellery creative photoshoot, jewellery custom sizing, AI jewellery photoshoot, jewellery marketing photoshoot, jewellery Instagram sizing, jewellery e-commerce photoshoot",
  },
  {
    slug: "hd-vs-ultra-hd-vs-4k-vs-print-master-jewellery-photo",
    title: "Photo Touch-Up for Jewellery E-commerce — HD vs Ultra-HD vs 4K vs Print Master",
    tldr: "HD 1024px for Instagram + WhatsApp broadcasts. Ultra-HD 2048px for e-commerce product tiles + retail websites. 4K 4096px for PDF lookbooks + printed flyers up to A5. 8K Print Master ~7680px for banners, billboards and 300 DPI large-format. A cost-vs-use-case chart for atelier marketing teams.",
    category: "workflow",
    published: "2026-02-25",
    readMinutes: 5,
    keywords: "jewellery photo touch-up tier, HD jewellery photo, 4K jewellery photo, 8K jewellery print, jewellery e-commerce photo enhancement, jewellery photo AI tier, jewellery print master",
  },
  // -------------------------------------------------------------------
  // 2026-02-25 SEO Execution Brief §29 — 11 supporting cluster articles.
  // -------------------------------------------------------------------
  {
    slug: "how-to-edit-jewellery-product-photos-with-ai",
    title: "How to Edit Jewellery Product Photos with AI — Step-by-Step",
    tldr: "Six practical steps to take a raw jewellery photograph and turn it into a catalogue-quality product image with AI: background, lighting, colour, cleanup, sharpness and export tier. Screenshots from the Facetra pipeline for each step.",
    category: "workflow",
    published: "2026-02-25",
    readMinutes: 6,
    keywords: "edit jewellery photos AI, edit jewelry product photos AI, AI jewellery photo editing tutorial, jewellery photo editing steps, how to edit jewellery photos, jewellery product photo editor AI",
  },
  {
    slug: "jewellery-photo-retouching-guide",
    title: "Jewellery Photo Retouching Guide — Gold, Diamond, Kundan, Jadau",
    tldr: "What actually needs retouching on a jewellery photo, style by style — gold colour warmth, diamond facet light-play, foil-back Polki reverse glow, Jadau uncut-stone diffuse light, and the six shortcuts an AI editor unlocks vs a hand retoucher.",
    category: "workflow",
    published: "2026-02-25",
    readMinutes: 7,
    keywords: "jewellery photo retouching, jewelry photo retouching, jewellery retouch guide, jewellery photo editor guide, AI jewellery retouching, jewellery product photo retouch",
  },
  {
    slug: "how-to-prepare-jewellery-images-for-ecommerce",
    title: "How to Prepare Jewellery Images for E-commerce — Specs, Sizes, Backgrounds",
    tldr: "Shopify, Amazon, Flipkart, Meesho, Nykaa Fashion and Ajio — each marketplace has its own image specs (aspect ratio, background, DPI). A cheat-sheet + the two-step AI pipeline that produces marketplace-ready images from any product photo.",
    category: "workflow",
    published: "2026-02-25",
    readMinutes: 7,
    keywords: "jewellery images for ecommerce, jewelry ecommerce photos, jewellery Shopify images, jewellery Amazon images, jewellery marketplace image specs, prepare jewellery photos ecommerce",
  },
  {
    slug: "how-to-create-jewellery-catalog-with-ai",
    title: "How to Create a Jewellery Catalog with AI — From SKU Photos to PDF Lookbook",
    tldr: "The complete workflow — photograph every SKU once on any camera, run AI normalisation for consistent backgrounds and lighting, group by collection, apply a catalog theme, export as PDF lookbook or ZIP asset kit. Screenshots for each stage.",
    category: "workflow",
    published: "2026-02-25",
    readMinutes: 8,
    keywords: "create jewellery catalog AI, jewellery catalog generator, jewelry catalog AI tutorial, AI jewellery catalogue, jewellery product catalog AI, how to make jewellery catalog",
  },
  {
    slug: "ai-jewellery-product-photography",
    title: "AI Jewellery Product Photography — What Works and What Doesn't",
    tldr: "AI product photography works well for background, lighting, colour and detail enhancement. It still needs a real camera photograph as input — no text-to-image workflow ships production-ready SKU images yet. A hands-on rundown of the six edit categories that ship reliably.",
    category: "comparison",
    published: "2026-02-25",
    readMinutes: 6,
    keywords: "AI jewellery product photography, AI jewelry product photography, AI product photography jewellery, AI jewellery photography software, jewellery photography AI tool",
  },
  {
    slug: "how-to-create-consistent-jewellery-catalog-images",
    title: "How to Create Consistent Jewellery Catalog Images Across 100+ SKUs",
    tldr: "Consistency across a catalog is a batch-mode job — one background, one light temperature, one framing rectangle, one style pipeline per style category. How to queue 100+ SKUs in a single Facetra batch and export a coherent collection in one afternoon.",
    category: "workflow",
    published: "2026-02-25",
    readMinutes: 6,
    keywords: "consistent jewellery catalog images, jewellery collection consistency, batch jewellery photo editing, jewellery catalog batch AI, 100 SKU jewellery catalog",
  },
  {
    slug: "how-to-create-jewellery-design-portfolio",
    title: "How to Create a Jewellery Design Portfolio — From Blank Slate to PDF Lookbook",
    tldr: "The 8-step portfolio workflow — pick a collection theme, moodboard it, generate hero renders, add multi-view sheets and technical drawings, write design briefs, curate to 10-20 pieces, apply a lookbook theme, export PDF + ZIP. Every step done in one browser tab.",
    category: "workflow",
    published: "2026-02-25",
    readMinutes: 8,
    keywords: "create jewellery design portfolio, jewellery portfolio how to, jewellery designer portfolio guide, jewellery portfolio tutorial, AI jewellery portfolio, jewellery portfolio maker",
  },
  {
    slug: "jewellery-design-portfolio-with-technical-drawing",
    title: "Jewellery Design Portfolio with Technical Drawing — The Hire-Ready Format",
    tldr: "Recruiters and atelier owners hire the designer whose portfolio proves manufacturability, not just artistry. Why every piece in a portfolio should include a multi-view sheet + a technical drawing + a BOM. Template for a 5-piece student thesis and a 20-piece atelier reel.",
    category: "workflow",
    published: "2026-02-25",
    readMinutes: 7,
    keywords: "jewellery portfolio with technical drawing, jewellery designer portfolio technical, jewellery portfolio production drawing, jewellery portfolio hire, jewellery designer portfolio CAD",
  },
  {
    slug: "how-to-present-jewellery-design-concepts",
    title: "How to Present Jewellery Design Concepts to Clients and Ateliers",
    tldr: "The 4-step client-approval flow — moodboard first (align on aesthetic), rough concept sketches (align on silhouette), photoreal render + multi-view (align on manufacture), BOM + design brief (align on cost). Why skipping stages loses commissions.",
    category: "workflow",
    published: "2026-02-25",
    readMinutes: 6,
    keywords: "present jewellery design concepts, jewellery design presentation, jewellery client presentation, jewellery designer client meeting, jewellery concept approval, jewellery design pitch",
  },
  {
    slug: "jewellery-technical-drawing-guide",
    title: "Jewellery Technical Drawing Guide — Views, Dimensions, Materials, Notes",
    tldr: "A technical drawing is a legibility contract between designer and CAD bench. What every technical sheet must include (views, dimensions, materials, stone spec, notes), what to leave out (marketing copy, mood text), and the three convention differences between Indian and Western jewellery drawings.",
    category: "workflow",
    published: "2026-02-25",
    readMinutes: 8,
    keywords: "jewellery technical drawing guide, jewellery drafting guide, jewellery specification sheet, jewellery drawing conventions, jewellery technical drawing standards, how to draft jewellery",
  },
  {
    slug: "front-side-top-view-jewellery-drawing",
    title: "Front, Side, Top and Back Views on a Jewellery Drawing — When to Use Which",
    tldr: "Ring: Front + Top + Side. Necklace: Front. Earring: Front + Side. Pendant: Front + Side + Back. Bangle: Front + Top + Side. Why each jewellery type has its own required view set and how the CAD bench reads them.",
    category: "workflow",
    published: "2026-02-25",
    readMinutes: 5,
    keywords: "front side top view jewellery drawing, jewellery multi-view drawing, jewellery orthographic views, jewellery drawing views per type, jewellery drafting views, jewellery view standards",
  },
];

export function getArticle(slug) {
  return ARTICLES.find((a) => a.slug === slug) || null;
}

export function getRelatedArticles(slug, limit = 3) {
  const cur = getArticle(slug);
  if (!cur) return [];
  return ARTICLES.filter((a) => a.slug !== slug && a.category === cur.category)
    .slice(0, limit)
    .concat(
      ARTICLES.filter((a) => a.slug !== slug && a.category !== cur.category).slice(0, limit),
    )
    .slice(0, limit);
}
